/**
 * Condition Resolver — "Start Anywhere" (10F)
 *
 * Computes the resolved condition state for any node in the prioritization
 * hierarchy — cycle, track, session, or tool — by walking up the hierarchy
 * and gathering what is met, inherited, missing, blocked, or conflicting.
 *
 * This implements the Condition Resolution Model from 10F. It USES existing
 * evaluators as data sources; it does not replace them.
 *
 * Five condition states:
 *   met         — satisfied at this level          → silent
 *   inherited   — satisfied by a parent object     → silent (but inspectable)
 *   missing     — not satisfied, needs action      → surface with named blocker card
 *   blocked     — can't act until prior is fixed   → surface as dependency
 *   conflicting — set at multiple levels, clash    → surface with resolution path
 */

import { getSafeSupabaseClient } from './supabaseSync';
import { evaluateTrackReadiness } from './trackReadinessEvaluator';
import type { TrackReadinessStatus } from './trackReadinessEvaluator';

// ============================================================================
// Core Types
// ============================================================================

export type ConditionState = 'met' | 'inherited' | 'missing' | 'blocked' | 'conflicting';

export interface ResolvedCondition {
  /** Machine-readable code matching existing evaluator codes where possible */
  code: string;
  /** User-facing label — specific enough to act on */
  label: string;
  /** Where this condition originates in the hierarchy */
  source: 'workspace' | 'configuration' | 'cycle' | 'track' | 'session' | 'tool';
  /** Current resolution state */
  state: ConditionState;
  /** Which prior condition must be met first (for blocked state) */
  blockedBy?: string;
  /** Direct link to the surface where this can be resolved */
  remediationPath?: string;
  /** Readiness class per canon */
  readinessClass: 'required' | 'recommended' | 'context_dependent' | 'informational';
  /** Additional detail from underlying evaluator (shown in panel) */
  detail?: string;
}

export interface ResolutionResult {
  /** The node that was resolved */
  nodeType: 'cycle' | 'track' | 'session' | 'tool';
  nodeId: string;
  /** The context this node belongs to */
  contextId?: string;
  /** The active configuration (if any) */
  configurationSlug?: string;
  /** All resolved conditions */
  conditions: ResolvedCondition[];
  /** Only the conditions that need user attention (missing, blocked, conflicting) */
  actionableConditions: ResolvedCondition[];
  /** Whether the node is ready to use right now */
  ready: boolean;
  /** The single most important next action, if not ready */
  primaryNextAction?: {
    label: string;
    path: string;
    conditionCode: string;
  };
  /** Role-specific view — same conditions, filtered for the current user's role */
  roleView?: {
    role: UserRole;
    relevantConditions: ResolvedCondition[];
    canAct: boolean;
    waitingOn?: string;
  };
}

export type UserRole = 'architect' | 'facilitator' | 'participant' | 'reviewer' | 'observer';

// ============================================================================
// Condition Dependency Order
// ============================================================================

/**
 * Canonical dependency order for conditions.
 * A condition earlier in this list that is missing will mark all later
 * missing conditions as 'blocked' rather than 'missing'.
 * Users should never be told to lock criteria before selecting them.
 */
export const CONDITION_DEPENDENCY_ORDER = [
  'PARTICIPATION',     // S1 — must be first
  'POOL_EXISTS',       // S2a
  'POOL_NONEMPTY',     // S2b — blocked by S2a
  'POOL_LOCKED',       // S2c — blocked by S2b
  'CRITERIA_SELECTED', // S3a
  'CRITERIA_LOCKED',   // S3b — blocked by S3a
  'SCALES_DEFINED',    // S4a — blocked by S3b
  'SCALES_ACCEPTED',   // S4b — blocked by S4a
  'VALUES_SUFFICIENT', // S5a
  'VALUES_CONVERGED',  // S5b — blocked by S5a
  'ALTITUDE_SET',      // prerequisite for comparability
] as const;

export type CanonicalConditionCode = (typeof CONDITION_DEPENDENCY_ORDER)[number];

const CONDITION_LABELS: Record<string, string> = {
  PARTICIPATION: 'Participation not defined',
  POOL_EXISTS: 'No item pool exists',
  POOL_NONEMPTY: 'Item pool is empty',
  POOL_LOCKED: 'Item pool not locked',
  CRITERIA_SELECTED: 'No criteria selected',
  CRITERIA_LOCKED: 'Criteria not locked',
  SCALES_DEFINED: 'Value scales not defined',
  SCALES_ACCEPTED: 'Value scales not accepted',
  VALUES_SUFFICIENT: 'Not enough values entered',
  VALUES_CONVERGED: 'Values not converged',
  ALTITUDE_SET: 'Item altitude not set',
  WORKFLOW_CONFIGURED: 'No workflow configured for this cycle',
  TRACKS_EXIST: 'No tracks created for this cycle',
};

const CONDITION_MET_LABELS: Record<string, string> = {
  PARTICIPATION: 'Participation defined',
  POOL_LOCKED: 'Item pool locked',
  CRITERIA_LOCKED: 'Criteria locked',
  SCALES_DEFINED: 'Value scales defined',
  VALUES_SUFFICIENT: 'Values sufficient',
};

/** Which role(s) can fix each condition */
const CONDITION_OWNER_ROLES: Record<string, UserRole[]> = {
  PARTICIPATION: ['architect', 'facilitator'],
  POOL_EXISTS: ['architect', 'facilitator'],
  POOL_NONEMPTY: ['architect', 'facilitator'],
  POOL_LOCKED: ['architect', 'facilitator'],
  CRITERIA_SELECTED: ['architect', 'facilitator'],
  CRITERIA_LOCKED: ['architect', 'facilitator'],
  SCALES_DEFINED: ['architect', 'facilitator'],
  SCALES_ACCEPTED: ['architect', 'facilitator'],
  VALUES_SUFFICIENT: ['participant', 'facilitator'],
  VALUES_CONVERGED: ['architect', 'facilitator'],
  ALTITUDE_SET: ['architect', 'facilitator'],
  WORKFLOW_CONFIGURED: ['architect'],
  TRACKS_EXIST: ['architect'],
};

// ============================================================================
// Track Readiness → ResolvedCondition mapper
// ============================================================================

/**
 * Map a TrackReadinessStatus to ResolvedCondition[].
 * perspective controls whether satisfied conditions appear as 'met' (track-level view)
 * or 'inherited' (session/tool-level view, where the track is a parent).
 */
function mapTrackReadinessToConditions(
  status: TrackReadinessStatus,
  perspective: 'track' | 'session' | 'tool'
): ResolvedCondition[] {
  const conditions: ResolvedCondition[] = [];
  const source: ResolvedCondition['source'] = 'track';
  const metState: ConditionState = perspective === 'track' ? 'met' : 'inherited';

  // S1 — Participation
  if (status.s1_participation.ready) {
    conditions.push({
      code: 'PARTICIPATION',
      label: CONDITION_MET_LABELS.PARTICIPATION,
      source,
      state: metState,
      readinessClass: 'required',
    });
  } else {
    conditions.push({
      code: 'PARTICIPATION',
      label: CONDITION_LABELS.PARTICIPATION,
      source,
      state: 'missing',
      remediationPath: `/prioritization/tracks/${status.trackId}?tab=participation`,
      readinessClass: 'required',
      detail: status.s1_participation.blockers.join('; ') || undefined,
    });
  }

  // S2 — Pool (distinguish empty vs. not locked)
  if (status.s2_pool.ready) {
    conditions.push({
      code: 'POOL_LOCKED',
      label: CONDITION_MET_LABELS.POOL_LOCKED,
      source,
      state: metState,
      readinessClass: 'required',
    });
  } else {
    const blockerText = status.s2_pool.blockers.join(' ').toLowerCase();
    const isEmpty = blockerText.includes('empty') || blockerText.includes('no item');
    const code = isEmpty ? 'POOL_NONEMPTY' : 'POOL_LOCKED';
    conditions.push({
      code,
      label: CONDITION_LABELS[code],
      source,
      state: 'missing',
      remediationPath: `/prioritization/tracks/${status.trackId}?tab=pool`,
      readinessClass: 'required',
      detail: status.s2_pool.blockers.join('; ') || undefined,
    });
  }

  // S3 — Criteria (distinguish not selected vs. not locked)
  if (status.s3_criteria.ready) {
    conditions.push({
      code: 'CRITERIA_LOCKED',
      label: CONDITION_MET_LABELS.CRITERIA_LOCKED,
      source,
      state: metState,
      readinessClass: 'required',
    });
  } else {
    const blockerText = status.s3_criteria.blockers.join(' ').toLowerCase();
    const noCriteria =
      blockerText.includes('no criteria') ||
      blockerText.includes('no attribute') ||
      blockerText.includes('not defined');
    const code = noCriteria ? 'CRITERIA_SELECTED' : 'CRITERIA_LOCKED';
    conditions.push({
      code,
      label: CONDITION_LABELS[code],
      source,
      state: 'missing',
      remediationPath: `/prioritization/tracks/${status.trackId}?tab=criteria`,
      readinessClass: 'required',
      detail: status.s3_criteria.blockers.join('; ') || undefined,
    });
  }

  // S4 — Scales (live within the Criteria & scales tab)
  if (status.s4_scales.ready) {
    conditions.push({
      code: 'SCALES_DEFINED',
      label: CONDITION_MET_LABELS.SCALES_DEFINED,
      source,
      state: metState,
      readinessClass: 'required',
    });
  } else {
    conditions.push({
      code: 'SCALES_DEFINED',
      label: CONDITION_LABELS.SCALES_DEFINED,
      source,
      state: 'missing',
      remediationPath: `/prioritization/tracks/${status.trackId}?tab=criteria`,
      readinessClass: 'required',
      detail: status.s4_scales.blockers.join('; ') || undefined,
    });
  }

  // S5 — Values (distinguish insufficient vs. not converged)
  if (status.s5_values.ready) {
    conditions.push({
      code: 'VALUES_SUFFICIENT',
      label: CONDITION_MET_LABELS.VALUES_SUFFICIENT,
      source,
      state: metState,
      readinessClass: 'required',
    });
  } else {
    const blockerText = status.s5_values.blockers.join(' ').toLowerCase();
    const notConverged =
      blockerText.includes('converge') ||
      blockerText.includes('consensus') ||
      blockerText.includes('not agreed');
    const code = notConverged ? 'VALUES_CONVERGED' : 'VALUES_SUFFICIENT';
    conditions.push({
      code,
      label: CONDITION_LABELS[code],
      source,
      state: 'missing',
      remediationPath: `/prioritization/tracks/${status.trackId}?tab=values`,
      readinessClass: 'required',
      detail: status.s5_values.blockers.join('; ') || undefined,
    });
  }

  return conditions;
}

// ============================================================================
// Dependency blocking pass
// ============================================================================

/**
 * Walk conditions in dependency order. If a condition earlier in the order
 * is 'missing', mark all later 'missing' conditions as 'blocked'.
 * Users should never be asked to resolve a downstream condition before
 * the upstream one.
 */
function applyDependencyBlocking(conditions: ResolvedCondition[]): ResolvedCondition[] {
  const orderMap = new Map<string, number>();
  CONDITION_DEPENDENCY_ORDER.forEach((code, idx) => orderMap.set(code, idx));

  // Sort by canonical order (unknowns go last)
  const sorted = [...conditions].sort((a, b) => {
    const orderA = orderMap.get(a.code) ?? 999;
    const orderB = orderMap.get(b.code) ?? 999;
    return orderA - orderB;
  });

  // Find the first missing condition in order
  const firstMissingIdx = sorted.findIndex((c) => c.state === 'missing');
  if (firstMissingIdx === -1) return sorted;

  const firstMissingCode = sorted[firstMissingIdx].code;
  const firstMissingOrder = orderMap.get(firstMissingCode) ?? 999;

  // All later 'missing' conditions become 'blocked'
  return sorted.map((c) => {
    if (c.state !== 'missing') return c;
    const order = orderMap.get(c.code) ?? 999;
    if (order > firstMissingOrder) {
      return { ...c, state: 'blocked' as ConditionState, blockedBy: firstMissingCode };
    }
    return c;
  });
}

// ============================================================================
// User role resolution
// ============================================================================

/**
 * Resolve the current user's role in the given cycle/track context.
 * Checks: cycle owner (architect), cycle_participants role, track_participants role,
 * then falls back to participant (if member) or observer.
 */
export async function resolveUserRole(
  userId: string,
  cycleId?: string | null,
  trackId?: string | null
): Promise<UserRole> {
  const supabase = getSafeSupabaseClient();
  if (!supabase || (!cycleId && !trackId)) return 'observer';

  try {
    if (cycleId) {
      // Cycle owner is architect
      const { data: cycle } = await supabase
        .from('degap_cycles')
        .select('created_by')
        .eq('id', cycleId)
        .single();
      if (cycle?.created_by === userId) return 'architect';

      // Check cycle_participants for explicit role
      const { data: cp } = await supabase
        .from('cycle_participants')
        .select('role')
        .eq('cycle_id', cycleId)
        .eq('user_id', userId)
        .maybeSingle();
      if (cp?.role === 'facilitator') return 'facilitator';
    }

    if (trackId) {
      const { data: tp } = await supabase
        .from('track_participants')
        .select('role')
        .eq('track_id', trackId)
        .eq('user_id', userId)
        .maybeSingle();
      if (tp?.role === 'facilitator') return 'facilitator';
    }

    // Is the user a cycle member at all?
    if (cycleId) {
      const { data: member } = await supabase
        .from('cycle_participants')
        .select('user_id')
        .eq('cycle_id', cycleId)
        .eq('user_id', userId)
        .maybeSingle();
      if (member) return 'participant';
    }

    if (trackId) {
      const { data: member } = await supabase
        .from('track_participants')
        .select('user_id')
        .eq('track_id', trackId)
        .eq('user_id', userId)
        .maybeSingle();
      if (member) return 'participant';
    }
  } catch (err) {
    console.warn('[ConditionResolver] resolveUserRole error:', err);
  }

  return 'observer';
}

// ============================================================================
// Role view computation
// ============================================================================

function computeRoleView(
  role: UserRole,
  actionableConditions: ResolvedCondition[]
): ResolutionResult['roleView'] {
  // Conditions this role can directly fix
  const ownedConditions = actionableConditions.filter((c) =>
    CONDITION_OWNER_ROLES[c.code]?.includes(role)
  );

  // Conditions missing but owned by someone else
  const waitingConditions = actionableConditions.filter(
    (c) => c.state === 'missing' && !CONDITION_OWNER_ROLES[c.code]?.includes(role)
  );

  let waitingOn: string | undefined;
  if (ownedConditions.length === 0 && waitingConditions.length > 0) {
    const ownerRoles = new Set(
      waitingConditions.flatMap((c) => CONDITION_OWNER_ROLES[c.code] ?? [])
    );
    if (ownerRoles.has('architect')) waitingOn = 'cycle architect';
    else if (ownerRoles.has('facilitator')) waitingOn = 'facilitator';
    else waitingOn = 'another team member';
  }

  return {
    role,
    relevantConditions: ownedConditions,
    canAct: ownedConditions.length > 0,
    waitingOn,
  };
}

// ============================================================================
// Resolvers
// ============================================================================

/**
 * Resolve conditions for a cycle entry point.
 */
export async function resolveFromCycle(
  cycleId: string,
  userId: string,
  workspaceId: string
): Promise<ResolutionResult> {
  const supabase = getSafeSupabaseClient();
  const conditions: ResolvedCondition[] = [];

  if (supabase) {
    const { data: cycle } = await supabase
      .from('degap_cycles')
      .select('id, workflow_id, configuration_id, status, created_by, workspace_id')
      .eq('id', cycleId)
      .single();

    if (cycle) {
      let hasEffectiveWorkflow = Boolean(cycle.workflow_id);

      if (!hasEffectiveWorkflow) {
        const resolvedWorkspaceId = cycle.workspace_id ?? workspaceId ?? null;
        if (resolvedWorkspaceId) {
          const { data: workspace } = await supabase
            .from('workspaces')
            .select('default_workflow_id')
            .eq('id', resolvedWorkspaceId)
            .maybeSingle();
          hasEffectiveWorkflow = Boolean(workspace?.default_workflow_id);
        }
      }

      if (!hasEffectiveWorkflow) {
        conditions.push({
          code: 'WORKFLOW_CONFIGURED',
          label: CONDITION_LABELS.WORKFLOW_CONFIGURED,
          source: 'cycle',
          state: 'missing',
          remediationPath: `/prioritization/cycles/${cycleId}?fix=workflow`,
          readinessClass: 'required',
          detail: 'Choose a workflow on this cycle or set a workspace default workflow in Settings.',
        });
      }

      const { data: tracks } = await supabase
        .from('tracks')
        .select('id')
        .eq('cycle_id', cycleId);

      if (!tracks || tracks.length === 0) {
        conditions.push({
          code: 'TRACKS_EXIST',
          label: CONDITION_LABELS.TRACKS_EXIST,
          source: 'cycle',
          state: 'missing',
          remediationPath: `/prioritization/cycles/${cycleId}`,
          readinessClass: 'required',
        });
      }
    }
  }

  const userRole = await resolveUserRole(userId, cycleId, null);
  const withBlocking = applyDependencyBlocking(conditions);
  const actionable = withBlocking.filter(
    (c) => c.state === 'missing' || c.state === 'blocked' || c.state === 'conflicting'
  );
  const primary = actionable[0];

  return {
    nodeType: 'cycle',
    nodeId: cycleId,
    conditions: withBlocking,
    actionableConditions: actionable,
    ready: actionable.length === 0,
    primaryNextAction: primary
      ? {
          label: primary.label,
          path: primary.remediationPath ?? `/prioritization/cycles/${cycleId}`,
          conditionCode: primary.code,
        }
      : undefined,
    roleView: computeRoleView(userRole, actionable),
  };
}

/**
 * Resolve conditions for a track entry point.
 * Walks up to the cycle and workspace to gather inherited state.
 */
export async function resolveFromTrack(
  trackId: string,
  userId: string,
  _workspaceId: string
): Promise<ResolutionResult> {
  const trackReadiness = await evaluateTrackReadiness(trackId);

  if (!trackReadiness) {
    return {
      nodeType: 'track',
      nodeId: trackId,
      conditions: [],
      actionableConditions: [],
      ready: false,
      primaryNextAction: {
        label: 'Unable to evaluate track readiness',
        path: `/prioritization/tracks/${trackId}`,
        conditionCode: 'UNKNOWN',
      },
    };
  }

  // No dependency blocking at track level — conditions-met philosophy:
  // every area is independently reachable regardless of what else is missing.
  const conditions = mapTrackReadinessToConditions(trackReadiness, 'track');
  const actionable = conditions.filter(
    (c) => c.state === 'missing' || c.state === 'conflicting'
  );
  const primary = actionable[0];
  const userRole = await resolveUserRole(userId, trackReadiness.cycleId, trackId);

  return {
    nodeType: 'track',
    nodeId: trackId,
    conditions,
    actionableConditions: actionable,
    ready: actionable.length === 0,
    primaryNextAction: primary
      ? {
          label: primary.label,
          path: primary.remediationPath ?? `/prioritization/tracks/${trackId}`,
          conditionCode: primary.code,
        }
      : undefined,
    roleView: computeRoleView(userRole, actionable),
  };
}

/**
 * Resolve conditions for a session entry point.
 * Walks up through track → cycle → workspace.
 * Most common entry point for participants who receive a session link.
 */
export async function resolveFromSession(
  sessionId: string,
  userId: string,
  _workspaceId: string
): Promise<ResolutionResult> {
  const supabase = getSafeSupabaseClient();

  if (!supabase) {
    return {
      nodeType: 'session',
      nodeId: sessionId,
      conditions: [],
      actionableConditions: [],
      ready: false,
    };
  }

  // Step 1: Get session data (include item arrays to check session-level readiness)
  const { data: session } = await supabase
    .from('prioritization_sessions')
    .select('id, cycle_id, track_id, context_id, canonical_type, state, item_ids, isolated_items, participant_ids')
    .eq('id', sessionId)
    .single();

  if (!session) {
    return {
      nodeType: 'session',
      nodeId: sessionId,
      conditions: [],
      actionableConditions: [],
      ready: false,
      primaryNextAction: {
        label: 'Session not found',
        path: '/prioritization/sessions/list',
        conditionCode: 'UNKNOWN',
      },
    };
  }

  // Step 1b: Check session-level participants (canonical: session_participants table)
  // Fall back to participant_ids column for older sessions without the junction table.
  let sessionParticipantCount = (session as { participant_ids?: string[] }).participant_ids?.length ?? 0;
  try {
    const { count: spCount } = await supabase
      .from('session_participants')
      .select('user_id', { count: 'exact', head: true })
      .eq('session_id', sessionId)
      .eq('status', 'active');
    if (spCount != null) sessionParticipantCount = spCount;
  } catch {
    // session_participants table may not exist; keep fallback
  }

  const sessionItemCount =
    ((session as { item_ids?: string[] }).item_ids?.length ?? 0) +
    ((session as { isolated_items?: string[] }).isolated_items?.length ?? 0);

  // Step 2: Get track readiness (inherited state from track)
  let trackReadiness: TrackReadinessStatus | null = null;
  if (session.track_id) {
    trackReadiness = await evaluateTrackReadiness(session.track_id);
  }

  // Step 3: Build conditions. If the session has its own participants/items,
  // those session-level facts override the corresponding track conditions so we
  // don't surface false "Participation not defined" errors on an already-staffed session.
  const conditions: ResolvedCondition[] = [];

  if (trackReadiness) {
    const trackConditions = mapTrackReadinessToConditions(trackReadiness, 'session');

    for (const cond of trackConditions) {
      // PARTICIPATION: override with session-level participant count
      if (cond.code === 'PARTICIPATION' && sessionParticipantCount > 0) {
        conditions.push({ ...cond, state: 'inherited' });
        continue;
      }
      // POOL / item conditions: override when the session has its own items
      if ((cond.code === 'POOL_NONEMPTY' || cond.code === 'POOL_LOCKED' || cond.code === 'POOL_EXISTS') && sessionItemCount > 0) {
        conditions.push({ ...cond, state: 'inherited' });
        continue;
      }
      conditions.push(cond);
    }
  } else if (session.track_id) {
    // Track exists but readiness couldn't be evaluated
    conditions.push({
      code: 'PARTICIPATION',
      label: 'Track configuration not available — verify track setup',
      source: 'track',
      state: 'missing',
      remediationPath: `/prioritization/tracks/${session.track_id}`,
      readinessClass: 'required',
    });
  }

  // Step 4: Apply dependency blocking
  const withBlocking = applyDependencyBlocking(conditions);
  const actionable = withBlocking.filter(
    (c) => c.state === 'missing' || c.state === 'blocked' || c.state === 'conflicting'
  );
  const primary = actionable[0];

  // Step 5: Resolve user role
  const userRole = await resolveUserRole(
    userId,
    session.cycle_id ?? null,
    session.track_id ?? null
  );

  const fallbackPath = session.track_id
    ? `/prioritization/tracks/${session.track_id}`
    : '/prioritization';

  return {
    nodeType: 'session',
    nodeId: sessionId,
    contextId: session.context_id ?? undefined,
    conditions: withBlocking,
    actionableConditions: actionable,
    ready: actionable.length === 0,
    primaryNextAction: primary
      ? {
          label: primary.label,
          path: primary.remediationPath ?? fallbackPath,
          conditionCode: primary.code,
        }
      : undefined,
    roleView: computeRoleView(userRole, actionable),
  };
}

/**
 * Resolve conditions for a tool entry point.
 * Walks up through session → track → cycle → workspace.
 * Tool prerequisites are evaluated against the full inherited context.
 */
export async function resolveFromTool(
  toolId: string,
  sessionId: string,
  userId: string,
  workspaceId: string
): Promise<ResolutionResult> {
  // Inherit all session conditions (which already walk track/cycle)
  const sessionResult = await resolveFromSession(sessionId, userId, workspaceId);

  // The session result already carries the full S1–S5 hierarchy context.
  // Tool-specific issues (min/max items, scale compatibility) are evaluated by
  // the existing ToolPicker / useToolsReadiness store. The resolver's job here
  // is to surface those inherited conditions with the correct hierarchy labels
  // and remediation paths — not to re-evaluate tool specs from scratch.
  const allConditions = applyDependencyBlocking([...sessionResult.conditions]);

  const actionable = allConditions.filter(
    (c) => c.state === 'missing' || c.state === 'blocked' || c.state === 'conflicting'
  );
  const primary = actionable[0];
  const userRole = sessionResult.roleView?.role ?? 'observer';

  return {
    nodeType: 'tool',
    nodeId: toolId,
    contextId: sessionResult.contextId,
    conditions: allConditions,
    actionableConditions: actionable,
    ready: actionable.length === 0,
    primaryNextAction: primary
      ? {
          label: primary.label,
          path: primary.remediationPath ?? '/prioritization',
          conditionCode: primary.code,
        }
      : undefined,
    roleView: computeRoleView(userRole, actionable),
  };
}

/**
 * Main resolver — detects node type and delegates.
 * Use this when the entry point is unknown or variable.
 */
export async function resolveFromAnyNode(params: {
  nodeType: 'cycle' | 'track' | 'session' | 'tool';
  nodeId: string;
  sessionId?: string;
  userId: string;
  workspaceId: string;
}): Promise<ResolutionResult> {
  const { nodeType, nodeId, sessionId, userId, workspaceId } = params;

  switch (nodeType) {
    case 'cycle':
      return resolveFromCycle(nodeId, userId, workspaceId);
    case 'track':
      return resolveFromTrack(nodeId, userId, workspaceId);
    case 'session':
      return resolveFromSession(nodeId, userId, workspaceId);
    case 'tool':
      if (!sessionId) {
        throw new Error('[ConditionResolver] sessionId is required when nodeType is "tool"');
      }
      return resolveFromTool(nodeId, sessionId, userId, workspaceId);
  }
}
